import React from "react";
import {
  CircularProgress,
  useTheme,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { tokens } from "../theme";
import { RecordType } from "Models/filter-types";

// Generic Metric type covering both Deals and Contacts
interface Metric {
  stage: string;
  conversionRate?: number | null;
  winRate?: number;            // Deals only
  lostRate?: number;           // Deals only
  avgDaysInStage?: number;
  avgDaysToWon?: number;       // Deals only
  avgDaysToNext?: number;      // Contacts only
  avgMarketingActivities?: number; // Deals only
  avgSalesActivities?: number;     // Deals only
}

interface Column {
  label: string;
  key: keyof Metric;
}

interface PerformanceMetricsProps {
  stages?: Metric[];
  loading?: boolean;
  recordType: RecordType;
}

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  stages,
  loading,
  recordType,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Columns are dynamic depending on recordType
  const columns: Column[] =
    recordType === RecordType.DEALS
      ? [
          { label: "Conversion Rate From Stage", key: "conversionRate" },
          { label: "Win Rate From Stage", key: "winRate" },
          { label: "Lost Rate From Stage", key: "lostRate" },
          { label: "Avg. Days in Stage", key: "avgDaysInStage" },
          { label: "Avg. Days To Won From Stage", key: "avgDaysToWon" },
          // { label: "Avg. # of Marketing Activities From Stage", key: "avgMarketingActivities" },
          { label: "Avg. # of Sales Activities From Stage", key: "avgSalesActivities" },
        ]
      : [
          { label: "Conversion Rate From Stage", key: "conversionRate" },
          { label: "Avg. Days in Stage", key: "avgDaysInStage" },
          { label: "Avg. Days To Next Stage", key: "avgDaysToNext" },
        ];

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        p: 3,
        my: 2,
        ml: 2,
        mr: 3,
        bgcolor: "#FFFFFF",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, px: 3 }}>
        <Typography
          variant="subtitle1"
          color={colors.gray[200]}
          bgcolor={colors.gray[900]}
          fontWeight="bold"
          sx={{ p: 1, px: 3, display: "inline-block" }}
        >
          Performance Metrics
        </Typography>

        {loading && <CircularProgress size={24} sx={{ color: colors.primary[500] }} />}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: "none", py: 2, pl: 3, minWidth: 150 }}>
                <Typography sx={{ fontSize: "0.875rem", color: "#6B7280" }}>Stage</Typography>
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.key} align="center" sx={{ borderBottom: "none", py: 2 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {stages?.map((metric) => (
              <TableRow
                key={metric.stage}
                sx={{
                  "&:nth-of-type(even)": { bgcolor: "#F9FAFB" },
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ py: 2, pl: 3 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
                    {metric.stage}
                  </Typography>
                </TableCell>

                {columns.map((column) => (
                  <TableCell key={column.key} align="center" sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: "0.875rem", color: "#111827", fontWeight: 500 }}>
                      {metric[column.key] != null
                        ? `${metric[column.key]}${
                            column.key.includes("Rate") ? "%" : ""
                          }${column.key.includes("Days") ? " days" : ""}`
                        : "-"}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default PerformanceMetrics;
